import { orderBy } from 'lodash';
import React, { useContext, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { FaMinusCircle, FaPlus, FaTimes, FaUsers } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { selectActive } from "../common/slices/location";
import { createTimeSheet } from "../common/slices/timesheet";
import { ErrorContext } from "../ErrorContext";
import { BlackButton, ButtonBase, LightButton, WhiteButton } from "./Buttons";
import Divider from "./Divider";
import FormDatePicker from "./FormDatePicker";
import FormSelectCrewMember from "./FormSelectCrewMember";
import FormSelectCustomer from "./FormSelectCustomer";
import FormSelectScopeOfWork from "./FormSelectScopeOfWork";
import FormTextInput from "./FormTextInput";
import Modal from "./Modal";
import ModalGroupAddTimeEntry from "./ModalGroupAddTimeEntry";
import Row from "./Row";
import { H1, Pmb } from "./Typography";

const marshall = (input) => ({
    ...input,
    customerId: input.customer ? { label: input.customer.name, value: input.customer.id } : null,
    foremanId: input.foreman ? { label: input.foreman.active ? input.foreman.name : `(x) ${input.foreman.name}`, value: input.foreman.id } : null,
    timeEntries: {
        //Order time entries by crew member name
        items: orderBy(input.timeEntries?.items, 'crewMember.name').map(te => ({
            ...te,
            customerId: te.customer ? { label: te.customer.name, value: te.customer.id } : null,
            crewMemberId: te.crewMember ? { label: te.crewMember.active ? te.crewMember.name : `(x) ${te.crewMember.name}`, value: te.crewMember.id } : null,
            scopeId: te.scope ? { label: te.scope.name, value: te.scope.id } : null
        }))
    }
})

const unmarshall = (input) => ({
    ...input,
    customerId: input.customerId?.value,
    foremanId: input.foremanId?.value,
    timeEntries: {
        items: input.timeEntries?.items?.map(te => ({
            ...te,
            customerId: te.customerId?.value,
            crewMemberId: te.crewMemberId?.value,
            scopeId: te.scopeId?.value,
        }))
    }
})

const RowDeleteButton = (props) => (
    <ButtonBase {...props} style={{ padding: '1rem' }} />
)

const FormTimeEntryInput = ({ control, name = "timeEntries.items", errors, rules, locationId, showAll }) => {
    const [showGroupAdd, setShowGroupAdd] = useState(false);
    const { fields, append, remove } = useFieldArray({
        rules,
        control,
        name,
        keyName: "keyId",
    })

    const formProps = { control, errors, locationId }

    const customerId = useWatch({ control, name: 'customerId' })

    const onDelete = (item, index) => {
        remove(index);
    }

    return (
        <div>
            <Row>
                <Pmb style={{ width: '3rem' }} />
                <Pmb style={{ flex: 1 }}>Customer</Pmb>
                <Pmb style={{ flex: 1 }}>Crew Member</Pmb>
                <Pmb style={{ flex: 1 }}>Scope of Work</Pmb>
                <Pmb style={{ flex: 1 }}>Hours</Pmb>
            </Row>

            {
                fields.map((item, index) => (
                    <Row key={item.keyId} style={{marginBottom: '0.5rem'}}>
                        <RowDeleteButton onClick={() => onDelete(item, index)} disabled={item.status !== "PENDING"}><FaMinusCircle color="#000" /></RowDeleteButton>
                        <FormSelectCustomer name={`${name}.${index}.customerId`} {...formProps} rules={{ required: true }} disabled={item.status !== "PENDING"} />
                        <FormSelectCrewMember name={`${name}.${index}.crewMemberId`} {...formProps} rules={{ required: true }} disabled={item.status !== "PENDING"} showAll={showAll} />
                        <FormSelectScopeOfWork name={`${name}.${index}.scopeId`} {...formProps} rules={{ required: true }} disabled={item.status !== "PENDING"} />
                        <FormTextInput name={`${name}.${index}.hours`} placeholder="Enter hours" {...formProps} rules={{ required: true, min: 0, max: 24, pattern: /^([0-9]+([.][0-9]*)?|[.][0-9]+)$/i }} disabled={item.status !== "PENDING"} />
                    </Row>))
            }
            <Row>
                <WhiteButton disabled={!customerId} style={{ marginTop: '1rem' }} onClick={() => append({
                    status: "PENDING",
                    customerId,
                })}><FaPlus /> Add time entry</WhiteButton>
                <WhiteButton disabled={!customerId} style={{ marginTop: '1rem' }} onClick={() => setShowGroupAdd(true)}><FaUsers /> Add from group</WhiteButton>
            </Row>
            {
                showGroupAdd && <ModalGroupAddTimeEntry onClose={({ group, hours, scopeId }) => {
                    setShowGroupAdd(false)
                    group?.members.map(crewMember => append({
                        customerId,
                        crewMemberId: { value: crewMember.id, label: crewMember.name },
                        status: "PENDING",
                        hours,
                        scopeId
                    }))
                }} />
            }
        </div>
    )
}

const ModalEditTimesheet = ({ timesheet, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const formValues = marshall(timesheet);
    const locationId = useSelector(selectActive);

    const {
        control,
        formState: { errors, isSubmitting, isValid },
        handleSubmit
    } = useForm({
        defaultValues: { date: new Date() },
        values: { ...formValues, locationId },
    })

    const formProps = { control, errors, locationId }
    const hasSyncedRows = formValues?.timeEntries?.items?.reduce((result, te) => { return result || te.status !== "PENDING" }, false);
    const showAll = timesheet?.id;

    const onSave = async (data) => {
        try {
            await dispatch(createTimeSheet(unmarshall(data)));
            onClose();
            toast.success("Saved Time Sheet");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to save time sheet", forSupport: ex.message })
        }
    }

    return (
        <Modal show size="75%" zindex='1200'>
            <Row>
                <H1>{timesheet.id ? "Edit" : "Create"} Time Sheet</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }}><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>

            <Row>
                <FormSelectCustomer {...formProps} name="customerId" label="Customer" rules={{ required: true }} />
                <div style={{ flex: 2 }} />
            </Row>

            <Row>
                <FormDatePicker {...formProps} name="date" label="Date" rules={{ required: true }} showTimeInput dateFormat="yyyy-MM-dd 'at' h:mm aa" />
                <FormSelectCrewMember {...formProps} name="foremanId" label="Foreman" rules={{ required: true }} showAll={showAll} />
            </Row>


            <Divider.Horizontal />

            <FormTimeEntryInput {...formProps} rules={{ minLength: 1 }} showAll={showAll} />

            <Divider.Horizontal />
            <Row>
                <BlackButton onClick={handleSubmit(onSave)} disabled={isSubmitting || !isValid || hasSyncedRows}>Save</BlackButton>
                <LightButton onClick={onClose} disabled={isSubmitting}>Cancel</LightButton>
            </Row>

        </Modal>
    );
};

export default ModalEditTimesheet;
